(() => {
  if (window.__purchaseLimitGuardInstalled) return;
  window.__purchaseLimitGuardInstalled = true;

  const root = document.getElementById('purchase-limit-data');
  if (!root) return;

  const MESSAGE_TEMPLATE = root.dataset.message || 'You can only add up to [limit] of this item.';
  const TOAST_MS = 4000;
  const CART_PATHS = ['/cart/add', '/cart/change', '/cart/update'];

  /* ---------- configuration ----------
     Limits live on each variant (custom.purchase_limit) and reach the page as hidden
     [data-limit-variant] markers wherever a product renders. Rescanned on every DOM
     sync, so markers that arrive with re-rendered sections are picked up too. */

  /** variantId -> { max, title, productId } */
  const limits = new Map();
  /** productId -> { variants: Set<variantId>, total: number of variants the product has } */
  const limitedProducts = new Map();

  const scanMarkers = () => {
    for (const el of document.querySelectorAll('[data-limit-variant]')) {
      const variantId = String(el.dataset.limitVariant ?? '').trim();
      const max = Number.parseInt(el.dataset.limitMax ?? '', 10);
      if (!variantId || !Number.isFinite(max) || max < 0) continue;

      const productId = String(el.dataset.limitProduct ?? '').trim();
      limits.set(variantId, { max, title: el.dataset.limitTitle ?? '', productId });

      if (!productId) continue;
      const entry = limitedProducts.get(productId) ?? { variants: new Set(), total: 0 };
      entry.variants.add(variantId);
      entry.total = Number.parseInt(el.dataset.limitProductVariants ?? '', 10) || 0;
      limitedProducts.set(productId, entry);
    }
  };

  /* ---------- cart state ----------
     Mirrors the cart so a limit check never needs a round trip. Every line is kept,
     limited or not, because a marker can turn up after the cart was read. Rebuilt from
     /cart.js on load and after every mutation the theme announces. */

  const originalFetch = window.fetch.bind(window);

  /** variantId -> quantity currently in the cart */
  const inCart = new Map();
  /** line key -> variantId, for /cart/change.js calls addressed by key */
  const lineVariant = new Map();
  /** variantId of each line, by position, for calls addressed by 1-based index */
  const lineOrder = [];
  /** Quantity each line holds now, keyed by line key, variant id and "#index" alike,
      so an absolute-quantity call can hold a variant's other lines constant. */
  const lineQuantities = new Map();

  const limitFor = (variantId) => limits.get(variantId)?.max ?? Infinity;
  const quantityInCart = (variantId) => inCart.get(variantId) ?? 0;
  const remainingFor = (variantId) => Math.max(0, limitFor(variantId) - quantityInCart(variantId));

  const readCart = async () => {
    const routeRoot = window.Shopify?.routes?.root ?? '/';
    const response = await originalFetch(`${routeRoot}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js responded ${response.status}`);
    return response.json();
  };

  const refreshCart = async () => {
    try {
      const cart = await readCart();

      inCart.clear();
      lineVariant.clear();
      lineQuantities.clear();
      lineOrder.length = 0;

      (cart.items ?? []).forEach((item, index) => {
        const variantId = String(item.variant_id ?? item.id ?? '');
        const quantity = item.quantity ?? 0;

        lineOrder.push(variantId);
        if (item.key) {
          lineVariant.set(String(item.key), variantId);
          lineQuantities.set(String(item.key), quantity);
        }
        lineQuantities.set(`#${index + 1}`, quantity);
        // A variant split across lines (by properties) still has one cap.
        lineQuantities.set(variantId, (lineQuantities.get(variantId) ?? 0) + quantity);

        inCart.set(variantId, quantityInCart(variantId) + quantity);
      });

      syncDom();
    } catch (error) {
      console.error('purchase-limit-guard: could not read the cart', error);
    }
  };

  /* ---------- messaging ---------- */

  const toast = document.querySelector('[data-purchase-limit-toast]');
  const toastText = toast?.querySelector('[data-purchase-limit-toast-text]');
  let toastTimer = null;

  const messageFor = (variantId) =>
    MESSAGE_TEMPLATE.replaceAll('[limit]', String(limitFor(variantId))).replaceAll(
      '[title]',
      limits.get(variantId)?.title ?? ''
    );

  const announce = (variantId) => {
    const message = messageFor(variantId);

    document.dispatchEvent(
      new CustomEvent('theme:cart:limit-reached', {
        bubbles: true,
        detail: {
          variantId,
          productId: limits.get(variantId)?.productId ?? null,
          limit: limitFor(variantId),
          message,
        },
      })
    );

    if (!toast || !toastText) return;

    toastText.textContent = message;
    toast.hidden = false;
    // Next frame, so the element is painted hidden before the transition class lands.
    requestAnimationFrame(() => toast.classList.add('is-visible'));

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      toastTimer = window.setTimeout(() => {
        toast.hidden = true;
      }, 300);
    }, TOAST_MS);
  };

  /* ---------- request body parsing ---------- */

  const asEntries = (body) => {
    if (!body) return null;

    if (typeof body === 'string') {
      try {
        return { kind: 'json', value: JSON.parse(body) };
      } catch {
        return { kind: 'params', value: new URLSearchParams(body) };
      }
    }
    if (body instanceof URLSearchParams) return { kind: 'params', value: body };
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      return { kind: 'form', value: body };
    }
    if (typeof body === 'object') return { kind: 'json', value: body };

    return null;
  };

  /** Every {variantId, quantity} an /cart/add request is asking for. */
  const addRequests = (parsed) => {
    const requests = [];
    if (!parsed) return requests;

    const push = (id, quantity) => {
      if (id === undefined || id === null || id === '') return;
      const amount = Number.parseInt(quantity ?? 1, 10);
      requests.push({ id: String(id), quantity: Number.isFinite(amount) ? amount : 1 });
    };

    if (parsed.kind === 'json') {
      const { value } = parsed;
      if (!value || typeof value !== 'object') return requests;
      if (Array.isArray(value.items)) {
        for (const item of value.items) push(item?.id, item?.quantity);
      } else {
        push(value.id, value.quantity);
      }
      return requests;
    }

    const { value } = parsed;
    // FormData and URLSearchParams share get/getAll, and quick-add forms post a
    // single id/quantity pair. Bundle forms use items[N][id] instead.
    const indexed = new Map();
    for (const [key, raw] of value.entries()) {
      const match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (!match) continue;
      const [, index, field] = match;
      const entry = indexed.get(index) ?? {};
      entry[field] = raw;
      indexed.set(index, entry);
    }

    if (indexed.size > 0) {
      for (const entry of indexed.values()) push(entry.id, entry.quantity);
    } else {
      push(value.get('id'), value.get('quantity'));
    }

    return requests;
  };

  /** Resolve whatever /cart/change.js was handed — line key, variant id or index. */
  const variantForTarget = (target, line) => {
    if (target !== undefined && target !== null && target !== '') {
      const key = String(target);
      if (lineVariant.has(key)) return lineVariant.get(key);
      if (key.includes(':')) return key.split(':')[0];
      return key;
    }
    const index = Number.parseInt(line ?? '', 10);
    if (Number.isFinite(index) && index >= 1) return lineOrder[index - 1] ?? null;
    return null;
  };

  /* ---------- the three verdicts ---------- */

  /** /cart/add.js is additive: existing quantity plus what is being asked for. */
  const addBreaches = (parsed) => {
    const wanted = new Map();

    for (const { id, quantity } of addRequests(parsed)) {
      if (!limits.has(id)) continue;
      wanted.set(id, (wanted.get(id) ?? 0) + Math.max(0, quantity));
    }

    for (const [variantId, quantity] of wanted) {
      if (quantity > remainingFor(variantId)) return variantId;
    }
    return null;
  };

  /** /cart/change.js sets one line to an ABSOLUTE quantity. */
  const changeBreaches = (parsed) => {
    if (!parsed) return null;

    const read = (field) =>
      parsed.kind === 'json' ? parsed.value?.[field] : parsed.value.get(field);

    const variantId = variantForTarget(read('id'), read('line'));
    if (!variantId || !limits.has(variantId)) return null;

    const next = Number.parseInt(read('quantity') ?? '', 10);
    if (!Number.isFinite(next)) return null;

    // Only this line moves; the variant's other lines stay where they are.
    const others = quantityInCart(variantId) - currentQuantityOfTarget(read('id'), read('line'));
    return next + Math.max(0, others) > limitFor(variantId) ? variantId : null;
  };

  /** Quantity the addressed line holds right now, so siblings can be held constant. */
  const currentQuantityOfTarget = (target, line) => {
    if (target !== undefined && target !== null && target !== '') {
      const key = String(target);
      if (lineQuantities.has(key)) return lineQuantities.get(key);
    }
    const index = Number.parseInt(line ?? '', 10);
    if (Number.isFinite(index) && index >= 1) return lineQuantities.get(`#${index}`) ?? 0;
    return 0;
  };

  /** /cart/update.js sets ABSOLUTE quantities for the keys it names. */
  const updateBreaches = (parsed) => {
    if (!parsed) return null;

    /** [key, quantity] pairs addressed by line key or variant id. */
    let keyed = [];
    /** Quantities addressed by line position, as the cart page form posts them. */
    let positional = null;

    if (parsed.kind === 'json') {
      const { updates } = parsed.value ?? {};
      if (!updates) return null;
      if (Array.isArray(updates)) positional = updates;
      else keyed = Object.entries(updates);
    } else {
      const bare = [];
      for (const [key, raw] of parsed.value.entries()) {
        const match = key.match(/^updates\[(.*)\]$/);
        if (!match) continue;
        if (match[1] === '') bare.push(raw);
        else keyed.push([match[1], raw]);
      }
      if (bare.length > 0) positional = bare;
      if (keyed.length === 0 && positional === null) return null;
    }

    // Start from what the cart holds, then swap in each explicit override.
    const totals = new Map(inCart);

    const apply = (variantId, previous, rawQuantity) => {
      if (!variantId) return;
      const next = Number.parseInt(rawQuantity ?? '', 10);
      if (!Number.isFinite(next)) return;
      totals.set(variantId, Math.max(0, (totals.get(variantId) ?? 0) - previous + next));
    };

    for (const [key, quantity] of keyed) {
      apply(variantForTarget(key, null), currentQuantityOfTarget(key, null), quantity);
    }

    if (positional) {
      positional.forEach((quantity, index) => {
        apply(lineOrder[index], lineQuantities.get(`#${index + 1}`) ?? 0, quantity);
      });
    }

    for (const [variantId, quantity] of totals) {
      if (limits.has(variantId) && quantity > limitFor(variantId)) return variantId;
    }
    return null;
  };

  const verdictFor = (url, body) => {
    // Markers can land between DOM syncs; a request must never outrun them.
    scanMarkers();
    const parsed = asEntries(body);
    if (url.includes('/cart/add')) return addBreaches(parsed);
    if (url.includes('/cart/change')) return changeBreaches(parsed);
    if (url.includes('/cart/update')) return updateBreaches(parsed);
    return null;
  };

  const isCartMutation = (url) =>
    typeof url === 'string' && CART_PATHS.some((path) => url.includes(path));

  /* ---------- network interception ---------- */

  window.fetch = function patchedFetch(input, init) {
    try {
      const url = typeof input === 'string' ? input : input?.url ?? '';
      if (isCartMutation(url)) {
        const body = init?.body ?? (input && typeof input !== 'string' ? input.body : null);
        const blocked = verdictFor(url, body);
        if (blocked) {
          announce(blocked);
          return Promise.reject(new Error('Add to cart blocked: purchase limit reached.'));
        }
      }
    } catch (error) {
      console.error('purchase-limit-guard: fetch check failed', error);
    }
    return originalFetch(input, init);
  };

  /* XHR is patched too — not for this theme's own calls, which all use fetch, but
     because installed apps and older vendor scripts still reach the cart this way. */
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function patchedOpen(method, url) {
    this.__purchaseLimitUrl = url;
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function patchedSend(body) {
    let blocked = null;
    try {
      const url = this.__purchaseLimitUrl ?? '';
      if (isCartMutation(url)) blocked = verdictFor(url, body);
    } catch (error) {
      console.error('purchase-limit-guard: xhr check failed', error);
    }

    if (blocked) {
      announce(blocked);
      throw new Error('Add to cart blocked: purchase limit reached.');
    }
    return originalSend.apply(this, arguments);
  };

  /* Plain, non-AJAX forms still post the browser straight to the cart routes. */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target?.closest?.('form[action*="/cart/"]');
      if (!form) return;

      const action = form.getAttribute('action') ?? '';
      if (!isCartMutation(action)) return;

      const blocked = verdictFor(action, new FormData(form));
      if (!blocked) return;

      event.preventDefault();
      event.stopPropagation();
      announce(blocked);
    },
    true
  );

  /* ---------- DOM pass ---------- */

  const setClass = (el, token, on) => {
    if (!el || el.classList.contains(token) === on) return;
    el.classList.toggle(token, on);
  };

  /* Buy controls only. Deliberately narrow: a product form also holds variant swatches
     and quantity steppers, and marking those would catch clicks that have nothing to
     do with buying. The cart drawer's increase button counts too — pushing a line past
     the cap is the same breach as adding afresh. */
  const BUY_CONTROLS = [
    '[type="submit"]',
    '[name="add"]',
    '.add_to_cart_button',
    '.custom-add-cart',
    '[data-atc-btn]',
    '[data-buy-btn]',
    '[data-sticky-atc-btn]',
    '[data-cc-qty="up"]',
  ].join(', ');

  /* Capped controls are marked, never disabled. A disabled button swallows its own
     click, so the shopper gets a dead control and no explanation — which is exactly
     what the message exists to prevent. Marking keeps the click, and the capture-phase
     handler below turns it into the message. */
  const markBuyControls = (scope, atCap) => {
    if (scope.matches(BUY_CONTROLS)) setClass(scope, 'is-purchase-limit-reached', atCap);
    for (const control of scope.querySelectorAll(BUY_CONTROLS)) {
      setClass(control, 'is-purchase-limit-reached', atCap);
    }
  };

  /** The limited variant a cart-add form is currently pointed at, if any. */
  const formVariant = (form) => {
    const variantId = String(form.querySelector('[name="id"]')?.value ?? '');
    return limits.has(variantId) ? variantId : null;
  };

  /**
   * The limited variant a holder stands for. Cards and sticky bars only carry
   * data-product-id, which names a variant unambiguously only when the product has one.
   */
  const holderVariant = (holder) => {
    const own = String(holder.dataset.variantId ?? '');
    if (limits.has(own)) return own;

    const entry = limitedProducts.get(String(holder.dataset.productId ?? ''));
    if (!entry || entry.total !== 1 || entry.variants.size !== 1) return null;
    return entry.variants.values().next().value;
  };

  /**
   * The limited variant a control would put in the cart, or null.
   * Narrowest context first: a cart line names its variant outright, a product form
   * names the selected variant, and a card or sticky bar falls back to data-product-id.
   */
  const cappedVariantFor = (control) => {
    const line = control.closest('[data-cc-line][data-variant-id]');
    if (line) {
      const variantId = String(line.dataset.variantId ?? '');
      return limits.has(variantId) ? variantId : null;
    }

    const form = control.closest('form[action*="/cart/add"]');
    const fromForm = form ? formVariant(form) : null;
    if (fromForm) return fromForm;

    const holder = control.closest('[data-product-id]');
    return holder ? holderVariant(holder) : null;
  };

  const syncForms = () => {
    for (const form of document.querySelectorAll('form[action*="/cart/add"]')) {
      const variantId = formVariant(form);
      setClass(form, 'is-purchase-limited', Boolean(variantId));

      if (!variantId) {
        setClass(form, 'is-purchase-limit-reached', false);
        markBuyControls(form, false);
        continue;
      }

      const remaining = remainingFor(variantId);

      for (const input of form.querySelectorAll('input[name="quantity"]')) {
        const max = String(Math.max(1, remaining));
        if (input.getAttribute('max') !== max) input.setAttribute('max', max);
        if (remaining > 0 && Number(input.value) > remaining) input.value = String(remaining);
      }

      setClass(form, 'is-purchase-limit-reached', remaining === 0);
      markBuyControls(form, remaining === 0);
    }
  };

  /* Product cards and sticky buy bars both carry data-product-id, and the sticky bar
     sits outside any form, so it is only reachable here. */
  const syncCards = () => {
    for (const el of document.querySelectorAll('[data-product-id]')) {
      const variantId = holderVariant(el);
      if (!variantId) continue;

      const atCap = remainingFor(variantId) === 0;
      setClass(el, 'is-purchase-limit-reached', atCap);
      markBuyControls(el, atCap);
    }
  };

  const syncCartLines = () => {
    for (const line of document.querySelectorAll('[data-cc-line][data-variant-id]')) {
      const variantId = String(line.dataset.variantId ?? '');
      if (!limits.has(variantId)) continue;
      const atCap = quantityInCart(variantId) >= limitFor(variantId);
      setClass(line, 'is-purchase-limit-reached', atCap);
      markBuyControls(line, atCap);
    }
  };

  let syncScheduled = false;
  const syncDom = () => {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(() => {
      syncScheduled = false;
      try {
        scanMarkers();
        syncForms();
        syncCards();
        syncCartLines();
      } catch (error) {
        console.error('purchase-limit-guard: DOM sync failed', error);
      }
    });
  };

  /* A capped control still takes the click; this turns it into the message.
     Capture phase on document runs before the element's own listeners, so stopping
     propagation here also stops the theme's Alpine @click add-to-cart handler — the
     click is answered with an explanation instead of a silent no-op. */
  document.addEventListener(
    'click',
    (event) => {
      const control = event.target?.closest?.(BUY_CONTROLS);
      if (!control) return;

      const variantId = cappedVariantFor(control);
      if (!variantId || remainingFor(variantId) > 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      announce(variantId);
    },
    true
  );

  /* ---------- lifecycle ---------- */

  const init = () => {
    scanMarkers();
    refreshCart();

    new MutationObserver(syncDom).observe(document.body, { childList: true, subtree: true });

    // The theme announces its own cart mutations; app-driven ones surface as DOM churn.
    for (const eventName of ['eurus:cart:items-changed', 'theme:cart:updated', 'shopify:section:load']) {
      document.addEventListener(eventName, refreshCart);
    }

    // Variant switches change which product a form points at without touching the cart.
    document.addEventListener('change', (event) => {
      if (event.target?.closest?.('form[action*="/cart/add"]')) syncDom();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
